import React, { useState, useEffect, ReactElement } from "react";
import { gql, useQuery, useMutation } from "urql";
import { useParams, useLocation, Link } from "wouter";
import { cloneDeep, sortBy } from "lodash";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Alert, AlertVariant } from "../../../../components/src/Alert";
import cronValidate from "cron-validate";
import { TextField, SelectField, CheckboxItem, SubmitButton, JsonField, SearchMultipleSelectField } from "../../../../components/src/form";

import { MarkDownField } from "../../components/form/MarkDownField";
import { Page } from "../../../../components/src/Page";
import { isValidMongoDBObjectID } from "../../../../common/src/utils";
import { useAlertTimeOut } from "../../../../components/src/common/Utils";
import { TimezoneEnum } from "../../types.generated";

import { JobTypeEnum } from "../../../../scheduler/src/wire.types";

import { formikUrqlErrorFormater } from "../../../../components/src/common/formik-urql-error-helper";
import { GET_JOBS, UPSERT_JOB_DEFINITIONS } from "./JobsPage";
import { recursivelyRemoveKey } from "../../../../common/src";
import { REACT_APP_API_URI } from "../../env";

const cronOptions = {
    preset: "default", // You can choose a preset or customize it
    override: {
        useSeconds: false // Set to true if you want to validate seconds
    }
};

const validateCronExpression = (cronExpression: string | undefined | null) => {
    if (!cronExpression || typeof cronExpression !== "string") {
        return false;
    }

    try {
        const result = cronValidate(cronExpression, cronOptions);
        if (!result || typeof result.isValid !== "function") {
            return false;
        }

        const isValidMethod = result.isValid;
        return isValidMethod.call(result);
    } catch {
        return false;
    }
};

const defaultFormData = {
    _id: "",
    name: "",
    clientIds: ["582ae3939da3da2ebcea9f6c"], // Captor Fund Management AB
    type: JobTypeEnum.Manual,
    description: "",
    jobUserId: "59f82fd4cba1d51ef0b38d05", // JOBBOT_ID
    args: {},
    enabled: true,
    schedule: {
        day: "*",
        dayOfTheWeek: "*",
        hour: "*",
        minute: "*/20",
        month: "*"
    },
    timezone: TimezoneEnum.EuropeStockholm,
    url: "https://pyjobapi.captor.se/jobs/Template/run",
    createTimestamp: "",
    updateTimestamp: "",
    updateUserInfo: {
        name: ""
    }
};

const GET_ClIENTS = gql`
    query getClients($typeIn: [PartyType!], $statusIn: [PartyStatusEnum!]) {
        clients: parties(filter: { typeIn: $typeIn, statusIn: $statusIn }) {
            _id
            name
            dashName
            status
        }
    }
`;

export function JobPage(): ReactElement {
    const { id } = useParams<{ id: string }>();

    const [location, navigate] = useLocation();

    const [formData, setFormData] = useState(cloneDeep(defaultFormData));

    const isCreateMode = !!(id && id.toString() === "new");

    const [{ fetching: fetching_clients, error: error_clients, data: data_clients }] = useQuery({
        query: GET_ClIENTS,
        variables: { typeIn: "Client", statusIn: "Confirmed" },
        requestPolicy: "cache-and-network"
    });

    const [{ fetching, data, error }, _refetch] = useQuery({
        query: GET_JOBS,
        variables: {
            filter: {
                idIn: id
            }
        },
        requestPolicy: "network-only",
        pause: isCreateMode
    });

    const [_, upsertJobs] = useMutation(UPSERT_JOB_DEFINITIONS);

    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({ color: "info", visible: false, message: "" });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    useAlertTimeOut(alert, setAlert, 5);

    useEffect(() => {
        if (id && id !== "new" && data && data.schedulerJobDefinitions && data.schedulerJobDefinitions.length > 0) {
            const job = {
                _id: data.schedulerJobDefinitions[0]._id,
                name: data.schedulerJobDefinitions[0].name,
                clientIds: data.schedulerJobDefinitions[0].clientIds,
                type: data.schedulerJobDefinitions[0].type,
                description: data.schedulerJobDefinitions[0].description,
                jobUserId: data.schedulerJobDefinitions[0].jobUserId,
                args: data.schedulerJobDefinitions[0].args,
                enabled: data.schedulerJobDefinitions[0].enabled,
                schedule: data.schedulerJobDefinitions[0].schedule,
                timezone: data.schedulerJobDefinitions[0].timezone,
                url: data.schedulerJobDefinitions[0].url,
                createTimestamp: data.schedulerJobDefinitions[0].createTimestamp,
                updateTimestamp: data.schedulerJobDefinitions[0].updateTimestamp,
                updateUserInfo: data.schedulerJobDefinitions[0].updateUserInfo
            };
            setFormData(job);
        }
    }, [id, data]);

    if (fetching || fetching_clients)
        return (
            <div>
                <p>Loading...</p>
            </div>
        );

    if (error) return <pre>{JSON.stringify(error, null, 2)}</pre>;

    if (error_clients) return <pre>{JSON.stringify(error_clients, null, 2)}</pre>;

    if (id !== "new" && !data.schedulerJobDefinitions) {
        return <div>Job not found</div>;
    }

    if (!isCreateMode && data && data.instrument) {
        if (!isValidMongoDBObjectID(id)) {
            return <Page header={"id: ''" + id + "'' is not a valid id format"} />;
        }
    }

    const clients = data_clients.clients.map((d) => ({ key: d._id, value: d.name, text: d.name }));
    const sortedClients = sortBy(clients, "value");

    return (
        <div>
            <Link to={"/jobs"}>Jobs</Link>
            <div className="max-w-7xl mx-auto page mt-2">
                <div>
                    {formData ? (
                        <Formik
                            enableReinitialize={true}
                            initialValues={formData}
                            validationSchema={Yup.object({
                                name: Yup.string().trim().required("Name is required").min(1, "Name cannot be empty"),
                                clientIds: Yup.array().of(Yup.string()).required("Client ids are required"),
                                type: Yup.mixed().oneOf(Object.values(JobTypeEnum)).required("Job type is required"),
                                jobUserId: Yup.string().required("Job user ID is required"),
                                args: Yup.object().required("Args are required"),
                                enabled: Yup.boolean().required("Enabled is required"),
                                schedule: Yup.object({
                                    day: Yup.string()
                                        .required("Day is required")
                                        .test("is-valid-cron", "Invalid cron expression for day", (value) => {
                                            if (!value) return false;
                                            return validateCronExpression(`0 0 ${value} * *`);
                                        }),
                                    dayOfTheWeek: Yup.string()
                                        .required("Day of the week is required")
                                        .test("is-valid-cron", "Invalid cron expression for dayOfTheWeek", (value) => {
                                            if (!value) return false;
                                            return validateCronExpression(`0 0 * * ${value}`);
                                        }),
                                    hour: Yup.string()
                                        .required("Hour is required")
                                        .test("is-valid-cron", "Invalid cron expression for hour", (value) => {
                                            if (!value) return false;
                                            return validateCronExpression(`0 ${value} * * *`);
                                        }),
                                    minute: Yup.string()
                                        .required("Minute is required")
                                        .test("is-valid-cron", "Invalid cron expression for minute", (value) => {
                                            if (!value) return false;
                                            return validateCronExpression(`${value} * * * *`);
                                        }),
                                    month: Yup.string()
                                        .required("Month is required")
                                        .test("is-valid-cron", "Invalid cron expression for month", (value) => {
                                            if (!value) return false;
                                            return validateCronExpression(`0 0 * ${value} *`);
                                        })
                                }),
                                timezone: Yup.mixed().oneOf(Object.values(TimezoneEnum)).required("Timezone is required"),
                                url: Yup.string().url("Invalid URL format").required("URL is required")
                            })}
                            onSubmit={async (submitValues, { setSubmitting, setErrors }) => {
                                let input = cloneDeep(submitValues);

                                if (isCreateMode) {
                                    delete input["_id"];
                                }

                                delete input["createTimestamp"];
                                delete input["updateTimestamp"];
                                delete input["updateUserInfo"];

                                input = recursivelyRemoveKey(input, "__typename");
                                await upsertJobs({ input })
                                    .then((result) => {
                                        if ("error" in result && result.error) {
                                            const message = formikUrqlErrorFormater(result.error, setErrors);
                                            setAlert({ color: "danger", visible: true, message });
                                        } else {
                                            const docId = result.data.schedulerUpsertJobDefinitions[0]._id;
                                            setAlert({
                                                color: "success",
                                                visible: true,
                                                message: `job '${input.name}' upserted successfully!`
                                            });
                                            if (docId) {
                                                const pathname = location.split("?")[0].split("#")[0];
                                                const path = pathname.split("/");
                                                path.pop();
                                                path.push(docId);
                                                navigate(path.join("/"), { replace: true });
                                            }
                                        }
                                        return true;
                                    })
                                    .catch((error) => {
                                        setAlert({ color: "danger", visible: true, message: error.toString() });
                                    })
                                    .finally(() => {
                                        setSubmitting(false);
                                    });
                            }}
                        >
                            {({ isSubmitting }) => (
                                <div className="w-full">
                                    <Form autoComplete="off" className="form">
                                        <div className="flex flex-wrap -mx-2">
                                            <div className="w-full md:w-2/3 lg:w-2/3 px-2">
                                                <div className="flex flex-wrap -mx-2">
                                                    <div className="w-full sm:w-1/2 md:w-1/2 lg:w-1/2 px-2 mb-4">
                                                        <TextField
                                                            name="name"
                                                            label="Name"
                                                            type="text"
                                                            className=""
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                    <div className="w-full sm:w-1/2 md:w-1/2 lg:w-1/2 px-2 mb-4">
                                                        <SelectField
                                                            name="type"
                                                            label="Type"
                                                            options={Object.keys(JobTypeEnum).sort()}
                                                            className=""
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <div className="w-full sm:w-1/2 px-2 mb-4">
                                                        <SearchMultipleSelectField
                                                            name="clientIds"
                                                            label="Clients (owner)"
                                                            className=""
                                                            disabled={isSubmitting}
                                                            options={sortedClients}
                                                        />
                                                    </div>
                                                    <div className="w-full sm:w-1/2 md:w-1/2 lg:w-1/2 px-2 mb-4 mt-4">
                                                        <CheckboxItem name="enabled" label="Enabled" />
                                                    </div>
                                                </div>

                                                <div className="flex flex-wrap -mx-2">
                                                    <div className="w-full sm:w-3/4 md:w-3/4 lg:w-3/4 px-2 mb-4">
                                                        <JsonField name="args" label="Args" className="" rows={7} disabled={isSubmitting} />
                                                    </div>
                                                    <div className="w-full sm:w-1/4 md:w-1/4 lg:w-1/4 px-2 mb-4">
                                                        <SelectField
                                                            name="timezone"
                                                            label="Timezone"
                                                            options={Object.keys(TimezoneEnum).sort()}
                                                            className=""
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <div className="w-full sm:w-1/6 md:w-1/6 lg:w-1/6 px-2 mb-4">
                                                        <TextField
                                                            name="schedule.day"
                                                            label="Day"
                                                            type="text"
                                                            className=""
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                    <div className="w-full sm:w-1/6 md:w-1/6 lg:w-1/6 px-2 mb-4">
                                                        <TextField
                                                            name="schedule.month"
                                                            label="Month"
                                                            type="text"
                                                            className=""
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                    <div className="w-full sm:w-1/6 md:w-1/6 lg:w-1/6 px-2 mb-4">
                                                        <TextField
                                                            name="schedule.hour"
                                                            label="Hour"
                                                            type="text"
                                                            className=""
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                    <div className="w-full sm:w-1/6 md:w-1/6 lg:w-1/6 px-2 mb-4">
                                                        <TextField
                                                            name="schedule.minute"
                                                            label="Minute"
                                                            type="text"
                                                            className=""
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                    <div className="w-full sm:w-1/3 md:w-1/3 lg:w-1/3 px-2 mb-4">
                                                        <TextField
                                                            name="schedule.dayOfTheWeek"
                                                            label="Day of the week (0-6, Sunday=0)"
                                                            type="text"
                                                            className=""
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="flex flex-wrap -mx-2">
                                                    <div className="w-full sm:w-full md:w-1/3 lg:w-1/3 px-2 mb-4">
                                                        <TextField
                                                            name="createTimestamp"
                                                            label="Create timestamp"
                                                            type="text"
                                                            className=""
                                                            disabled={true}
                                                        />
                                                    </div>

                                                    <div className="w-full sm:w-full md:w-1/3 lg:w-1/3 px-2 mb-4">
                                                        <TextField
                                                            name="updateTimestamp"
                                                            label="Update timestamp"
                                                            type="text"
                                                            className=""
                                                            disabled={true}
                                                        />
                                                    </div>
                                                    <div className="w-full sm:w-full md:w-1/3 lg:w-1/3 px-2 mb-4">
                                                        <TextField
                                                            name="updateUserInfo.name"
                                                            label="Update user info"
                                                            type="text"
                                                            className=""
                                                            disabled={true}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <div className="w-full sm:w-1/2 md:w-1/2 lg:w-1/2 px-2 mb-4">
                                                        <TextField
                                                            name="jobUserId"
                                                            label="Job user id"
                                                            type="text"
                                                            className=""
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                    <div className="w-full sm:w-1/2 md:w-1/2 lg:w-1/2 px-2 mb-4">
                                                        <TextField name="_id" label="Id" type="text" className="" disabled={true} />
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <div className="w-full px-2 mb-4 mt-3">
                                                        <h5>Description</h5>
                                                        <MarkDownField
                                                            name="description"
                                                            graphqlApiBaseUrl={REACT_APP_API_URI}
                                                            label="Click to edit"
                                                            height={200}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap -mx-2">
                                                    <div className="w-full px-2 mb-4">
                                                        <TextField
                                                            name="url"
                                                            label="Url"
                                                            type="text"
                                                            className=""
                                                            disabled={isSubmitting}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {alert.visible ? (
                                            <Alert variant={alert.color} onClose={onDismissAlert} dismissible>
                                                {alert.message}
                                            </Alert>
                                        ) : null}

                                        <SubmitButton
                                            disabled={isSubmitting}
                                            label={isCreateMode ? "Create" : "Update"}
                                            className="btn btn-primary btn-lg"
                                        />
                                    </Form>
                                </div>
                            )}
                        </Formik>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
